import { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { AcademicCapIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

import { formatDate } from '../../common/formatDateText';
import { urlFlag } from '../../common/urlBase';
import { capitalizarPalabras } from '../../common/upperCaseWord';

const DAY_SYMBOLS = [
	{ day: 'lunes', simbolo: 'L' },
	{ day: 'martes', simbolo: 'M' },
	{ day: 'miércoles', simbolo: 'M' },
	{ day: 'jueves', simbolo: 'J' },
	{ day: 'viernes', simbolo: 'V' },
	{ day: 'sábado', simbolo: 'S' },
];

function scheduleRange(hours) {
	if (!hours?.length) return '—';
	return `${hours[0]?.time ?? ''}–${hours[hours.length - 1]?.time ?? ''}`;
}

const normalizeStatus = (status) => String(status || '').toLowerCase().trim();

const getStatusBadgeClass = (status) => {
	const s = normalizeStatus(status);
	if (s === 'abierto') return 'bg-sky-100 text-sky-700 border-sky-200';
	if (s === 'en curso') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
	return 'bg-gray-100 text-gray-600 border-gray-200';
};

function CourseStatusBadge({ status }) {
	if (!status) return null;
	return (
		<span
			className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide sm:text-[10px] ${getStatusBadgeClass(status)}`}
		>
			{status}
		</span>
	);
}

function CourseSelectCard({ course, courseId, checked, onToggle }) {
	const path = course.path;
	const flagUrl = path ? `${urlFlag}${path}` : null;
	const hoursLabel = scheduleRange(course.hours);
	const lang = course.language ? capitalizarPalabras(course.language) : 'Curso';
	const from = course.fromDate ? formatDate(course.fromDate) : '—';
	const to = course.toDate ? formatDate(course.toDate) : '—';
	const limitMembers = course.limitMembers;

	return (
		<button
			type='button'
			onClick={() => onToggle(courseId)}
			className={`
				group w-full rounded-xl border text-left transition-all duration-150
				${checked
					? 'border-indigo-500 bg-indigo-50/60 shadow-sm ring-1 ring-indigo-100'
					: 'border-gray-200 bg-white hover:border-indigo-200 hover:bg-gray-50'
				}
			`}
		>
			<div className='p-3 sm:p-4'>
				{/* Cabecera: bandera, título y acción */}
				<div className='flex gap-3'>
					<div className='shrink-0'>
						<div
							className={`
								flex h-11 w-11 sm:h-12 sm:w-12 items-center justify-center overflow-hidden
							`}
						>
							{flagUrl ? (
								<img src={flagUrl} alt='' className='h-full w-full object-cover' />
							) : (
								<div className='flex h-full w-full items-center justify-center bg-gradient-to-br from-indigo-100 to-violet-100'>
									<AcademicCapIcon className='h-6 w-6 sm:h-7 sm:w-7 text-indigo-600' aria-hidden />
								</div>
							)}
						</div>
					</div>

					<div className='min-w-0 flex-1'>
						<div className='flex items-start justify-between gap-2'>
							<div className='min-w-0 flex-1 space-y-1'>
								<p className='text-sm font-bold uppercase leading-tight tracking-wide text-gray-900 sm:text-base'>
									{lang}
								</p>
								<div className='flex flex-wrap items-center gap-1.5'>
									<span
										className={`inline-block h-2 w-2 shrink-0 rounded-full ${course.color || 'bg-indigo-500'}`}
										aria-hidden
									/>
									<span className='text-xs font-semibold uppercase text-indigo-700 sm:text-sm'>
										{course.level || '—'}
									</span>
									<CourseStatusBadge status={course.status} />
								</div>
								{course.identifier && (
									<p className='truncate font-mono text-[11px] text-gray-500 sm:text-xs'>
										{course.identifier}
									</p>
								)}
							</div>
							<span
								className={`
									flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-bold
									${checked ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500'}
								`}
								aria-hidden
							>
								{checked ? '✓' : '+'}
							</span>
						</div>
					</div>
				</div>

				{/* Detalle: filas legibles en móvil */}
				<div className='mt-3 space-y-2 border-t border-gray-100 pt-3'>
					<div className='grid grid-cols-1 gap-2 text-xs sm:grid-cols-3 sm:gap-3 sm:text-sm'>
						<div className='flex items-baseline justify-between gap-2 sm:flex-col sm:items-start sm:gap-0'>
							<span className='text-gray-500'>Profesor</span>
							<span className='font-medium capitalize text-gray-800 text-right sm:text-left'>
								{course.teacher || '—'}
							</span>
						</div>
						<div className='flex items-baseline justify-between gap-2 sm:flex-col sm:items-start sm:gap-0'>
							<span className='text-gray-500'>Horario</span>
							<span className='font-semibold text-gray-800'>{hoursLabel}</span>
						</div>
						<div className='flex items-baseline justify-between gap-2 sm:flex-col sm:items-start sm:gap-0'>
							<span className='text-gray-500'>Lugares</span>
							<span className='font-semibold text-gray-800'>{limitMembers}</span>
						</div>
					</div>

					<div className='flex flex-col gap-0.5 text-xs sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-2 sm:text-sm'>
						<span className='text-gray-500'>Periodo</span>
						<span className='font-medium text-gray-800'>
							<span className='text-gray-500'>Inicio </span>
							{from}
							<span className='mx-1.5 text-gray-300'>→</span>
							<span className='text-gray-500'>Fin </span>
							{to}
						</span>
					</div>

					<div className='flex flex-wrap items-center gap-1.5 pt-0.5'>
						{DAY_SYMBOLS.map(({ day, simbolo }) => {
							const active = course.days?.some((d) => d.day === day);
							return (
								<span
									key={day}
									className={`
										flex h-7 w-7 items-center justify-center rounded-md text-[10px] font-bold sm:h-6 sm:min-w-[1.5rem] sm:text-xs
										${active ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-400'}
									`}
									title={day}
								>
									{simbolo}
								</span>
							);
						})}
					</div>
				</div>
			</div>
		</button>
	);
}

export const CourseCheckboxSelector = ({
	courses = [],
	selectedCourseIds = [],
	setSelectedCourseIds,
	error = '',
}) => {
	const [emblaRef, emblaApi] = useEmblaCarousel({
		align: 'start',
		loop: false,
		skipSnaps: false,
		dragFree: true,
		containScroll: 'trimSnaps',
	});
	const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
	const [nextBtnDisabled, setNextBtnDisabled] = useState(true);
	const [selectedIndex, setSelectedIndex] = useState(0);

	const scrollPrev = useCallback(() => {
		if (emblaApi) emblaApi.scrollPrev();
	}, [emblaApi]);

	const scrollNext = useCallback(() => {
		if (emblaApi) emblaApi.scrollNext();
	}, [emblaApi]);

	const onSelect = useCallback((api) => {
		setPrevBtnDisabled(!api.canScrollPrev());
		setNextBtnDisabled(!api.canScrollNext());
		setSelectedIndex(api.selectedScrollSnap());
	}, []);

	useEffect(() => {
		if (!emblaApi) return;
		onSelect(emblaApi);
		emblaApi.on('reInit', onSelect);
		emblaApi.on('select', onSelect);
	}, [emblaApi, onSelect]);

	const toggleSelection = (courseId) => {
		setSelectedCourseIds((prev) => (
			prev.includes(courseId)
				? prev.filter((id) => id !== courseId)
				: [...prev, courseId]
		));
	};

	if (!courses.length) {
		return (
			<div className='space-y-2'>
				<p className='rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900'>
					No hay cursos con cupo disponibles para asignar. Revisa que existan cursos activos en el catálogo.
				</p>
				{error ? (
					<p className='flex items-center gap-1 text-sm text-red-600'>
						<svg className='h-4 w-4 shrink-0' fill='currentColor' viewBox='0 0 20 20' aria-hidden>
							<path
								fillRule='evenodd'
								d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z'
								clipRule='evenodd'
							/>
						</svg>
						{error}
					</p>
				) : null}
			</div>
		);
	}

	return (
		<div className='w-full'>
			<div className='mb-2 flex items-center justify-between'>
				<p className='text-sm text-gray-600'>
					Seleccionados:{' '}
					<span className='font-bold text-indigo-600'>{selectedCourseIds.length}</span>
				</p>
				{courses.length > 1 && (
					<p className='text-xs text-gray-400'>
						{selectedIndex + 1} / {courses.length}
					</p>
				)}
			</div>

			{courses.length > 1 && (
				<div className='mb-3 flex items-center justify-between gap-2 sm:justify-center sm:gap-6'>
					<button
						type='button'
						className='rounded-full bg-indigo-600 p-2 shadow-md transition-all hover:bg-indigo-700 active:scale-95 disabled:cursor-not-allowed disabled:bg-gray-300 sm:p-2.5'
						onClick={scrollPrev}
						disabled={prevBtnDisabled}
						aria-label='Curso anterior'
					>
						<ChevronLeftIcon className='h-5 w-5 text-white sm:h-6 sm:w-6' />
					</button>
					<div className='flex flex-1 flex-wrap items-center justify-center gap-1.5 sm:max-w-[55%] sm:gap-2'>
						{courses.map((_, index) => (
							<button
								type='button'
								key={`dot-${index}`}
								className={`rounded-full transition-all duration-300 ${selectedIndex === index ? 'h-2 w-7 bg-indigo-600 sm:h-2.5 sm:w-9' : 'h-2 w-2 bg-gray-300 hover:bg-gray-400 sm:h-2.5 sm:w-2.5'}`}
								onClick={() => emblaApi && emblaApi.scrollTo(index)}
								aria-label={`Ir al curso ${index + 1}`}
							/>
						))}
					</div>
					<button
						type='button'
						className='rounded-full bg-indigo-600 p-2 shadow-md transition-all hover:bg-indigo-700 active:scale-95 disabled:cursor-not-allowed disabled:bg-gray-300 sm:p-2.5'
						onClick={scrollNext}
						disabled={nextBtnDisabled}
						aria-label='Siguiente curso'
					>
						<ChevronRightIcon className='h-5 w-5 text-white sm:h-6 sm:w-6' />
					</button>
				</div>
			)}

			<div className='overflow-x-hidden' ref={emblaRef}>
				<div className={`flex ${courses.length > 1 ? 'justify-start' : 'justify-center'} gap-2 p-0.5`}>
					{courses.map((course) => {
						const courseId = course._id || course.id;
						const checked = selectedCourseIds.includes(courseId);
						return (
							<div
								key={courseId}
								className='min-w-0 shrink-0 flex-[0_0_100%] sm:flex-[0_0_92%] md:flex-[0_0_85%]'
							>
								<CourseSelectCard
									course={course}
									courseId={courseId}
									checked={checked}
									onToggle={toggleSelection}
								/>
							</div>
						);
					})}
				</div>
			</div>
			{error && <p className='mt-2 text-sm text-red-600'>{error}</p>}
		</div>
	);
};
